"use client";

import type { IconProps } from "@pamperedchef/design-system";
import NextImage, { type ImageProps as NextImageProps } from "next/image";
import { useTranslations } from "next-intl";

import { PDPBannerArea } from "@/composite/PDPBannerArea";
import type { Country } from "@/domain/country";
import type { ProductsResultSuccess } from "@/domain/product";
import type { DealCardReference, VideoAsset } from "@/types/references";
import { resolveImageProps } from "@/app/utilities/images";

import type { ProductDetailBannerBlockProps } from ".";

export interface ProductDetailBannerBlockClientProps extends ProductDetailBannerBlockProps {
	productData: ProductsResultSuccess;
	dealCards: DealCardReference[];
	country: Country;
	purchaseType: "oneTime" | "monthly";
}

/**
 * Featured Card block component
 */
export function ProductDetailBannerBlock({
	media_gallery,
	sku,
	productData,
	dealCards,
	country,
	purchaseType
}: ProductDetailBannerBlockClientProps): JSX.Element {
	const t = useTranslations("bannerArea");
	const tCart = useTranslations("cart");

	const product = productData.products[0];

	return (
		<PDPBannerArea<NextImageProps>
			gallery={media_gallery.asset_blocks.map((media) =>
				"blockType" in media && media.blockType === "video_asset"
					? {
							type: "video",
							videoSrc: media.video,
							poster: {
								component: NextImage,
								...resolveImageProps(
									"16:9",
									media as VideoAsset
								)
							}
						}
					: {
							type: "image",
							media: {
								component: NextImage,
								...resolveImageProps("1:1", media)
							}
						}
			)}
			product={{
				info: {
					country,
					purchaseType,
					name: product.name,
					itemNumber: sku,
					rating: {
						rating: product.starsRating,
						label: `(${product.numRatings})`
					},
					price: {
						display: product.salePrice,
						strikeThrough: product.listPrice
					},
					inventory: product.inventoryDetails?.inventoryLevel,
					inventoryLabels: {
						outOfStock: {
							title: t("outOfStockTitle"),
							description: t("outOfStockDescription")
						},
						lowStock: t("lowStock"),
						discontinued: {
							title: t("discontinuedTitle"),
							description: t("discontinuedDescription")
						}
					},
					urgentLabels: {
						bestSeller: t("bestSeller"),
						finalSale: t("finalSale"),
						newThisMonth: t("newThisMonth"),
						availableWhileSuppliesLast: t(
							"availableWhileSuppliesLast"
						)
					},
					deals: dealCards.map((dealCard) => ({
						icon: {
							name: dealCard.icon
								?.icon_name as unknown as IconProps["name"],
							size: "md"
						},
						label: dealCard.message
					}))
				},
				cta: {
					children: tCart("addToCart")
				}
			}}
			dealLabels={{
				singular: t("moreDeal"),
				plural: t("moreDeals")
			}}
			wishlistLabel={t("addToWishlist")}
			registryLabel={t("addToRegistry")}
			errorMessage={tCart("errorMessage")}
			alreadyAddedMessage={tCart("alreadyAddedMessage")}
		/>
	);
}
